import { forwardRef } from "react";
import { Coins, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useTranslation } from "react-i18next";
import { useCurrency } from "@/hooks/use-currency";
import { useCurrencyStore } from "@/lib/pricing/currency";
import { CURRENCIES, type CurrencyCode } from "@/lib/pricing/currency";
import { toast } from "sonner";

const AFRICA_CURRENCIES: CurrencyCode[] = ["NGN", "XOF", "XAF", "GNF", "KES", "GHS", "MAD", "ZAR", "EGP"];
const GLOBAL_CURRENCIES: CurrencyCode[] = ["USD", "GBP", "BRL", "MXN", "ARS", "COP", "IDR", "PHP", "VND", "INR"];

export const CurrencySwitcher = forwardRef<HTMLButtonElement>((_props, ref) => {
  const { localCurrency, detectedLocal, manuallySet, setOverride } = useCurrency();
  const clearManual = useCurrencyStore((s) => s.clearManual);
  const setCurrency = useCurrencyStore((s) => s.setCurrency);
  const { t } = useTranslation();

  const showLocalRow = !manuallySet && detectedLocal !== "EUR";

  const pick = (c: CurrencyCode) => {
    setOverride(c);
    toast.success(t("common.currencySwitched", { code: c }));
  };
  const resetToBase = () => {
    clearManual();
    setCurrency("EUR");
    toast.success(t("common.currencySwitched", { code: "EUR" }));
  };

  const isActive = (c: CurrencyCode) => localCurrency === c;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          ref={ref}
          variant="ghost"
          size="sm"
          aria-label={t("common.currency", "Currency")}
          className="gap-2"
        >
          <Coins className="h-4 w-4" />
          <span className="hidden sm:inline">{localCurrency}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[14rem]">
        <DropdownMenuLabel className="text-xs text-muted-foreground">
          {t("common.currency", "Currency")}
        </DropdownMenuLabel>

        <DropdownMenuItem onClick={resetToBase} className="flex items-center justify-between">
          <span className="flex items-center gap-2">
            <span className="w-10 font-mono text-xs text-muted-foreground">EUR</span>
            <span className="text-sm">{CURRENCIES.EUR.label}</span>
          </span>
          <span className="flex items-center gap-1">
            <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
              {t("common.base", "Base")}
            </span>
            {isActive("EUR") && <Check className="h-3.5 w-3.5" />}
          </span>
        </DropdownMenuItem>

        {showLocalRow && (
          <DropdownMenuItem
            onClick={() => pick(detectedLocal)}
            className="flex items-center justify-between"
          >
            <span className="flex items-center gap-2">
              <span className="w-10 font-mono text-xs text-muted-foreground">{detectedLocal}</span>
              <span className="text-sm">{CURRENCIES[detectedLocal].label}</span>
            </span>
            {isActive(detectedLocal) && <Check className="h-3.5 w-3.5" />}
          </DropdownMenuItem>
        )}

        {manuallySet && localCurrency !== "EUR" && (
          <DropdownMenuItem
            onClick={() => pick(localCurrency)}
            className="flex items-center justify-between"
          >
            <span className="flex items-center gap-2">
              <span className="w-10 font-mono text-xs text-muted-foreground">{localCurrency}</span>
              <span className="text-sm">{CURRENCIES[localCurrency].label}</span>
            </span>
            <Check className="h-3.5 w-3.5" />
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />

        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            {t("common.moreCurrencies", "More currencies")}
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="max-h-[60vh] overflow-y-auto">
            <DropdownMenuLabel className="text-xs text-muted-foreground">
              {t("common.currencyGroup.africa", "Africa")}
            </DropdownMenuLabel>
            {AFRICA_CURRENCIES.map((c) => (
              <DropdownMenuItem
                key={c}
                onClick={() => pick(c)}
                className="flex items-center justify-between"
              >
                <span className="flex items-center gap-2">
                  <span className="w-10 font-mono text-xs text-muted-foreground">{c}</span>
                  <span className="text-sm">{CURRENCIES[c].label}</span>
                </span>
                {isActive(c) && <Check className="h-3.5 w-3.5" />}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs text-muted-foreground">
              {t("common.currencyGroup.global", "Global")}
            </DropdownMenuLabel>
            {GLOBAL_CURRENCIES.map((c) => (
              <DropdownMenuItem
                key={c}
                onClick={() => pick(c)}
                className="flex items-center justify-between"
              >
                <span className="flex items-center gap-2">
                  <span className="w-10 font-mono text-xs text-muted-foreground">{c}</span>
                  <span className="text-sm">{CURRENCIES[c].label}</span>
                </span>
                {isActive(c) && <Check className="h-3.5 w-3.5" />}
              </DropdownMenuItem>
            ))}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
      </DropdownMenuContent>
    </DropdownMenu>
  );
});
CurrencySwitcher.displayName = "CurrencySwitcher";
